import type { IdpGroup } from '@repo/api-gw-sdk';
import { PermissionType } from '@repo/api-gw-sdk';
import { useMemo } from 'react';

import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { useRoles } from '@/contexts/useRoles';

export interface SamlGroupSupport {
  enabled: boolean;
  eligibleSamlGroups: IdpGroup[];
  samlGroupById: Record<string, IdpGroup>;
  providerNameById: Record<string, string>;
}

export const useSamlGroupSupport = (): SamlGroupSupport => {
  const dal = useDAL();
  const { ActionApprovalsSAML } = useFeatureFlags();
  const { rolesMap } = useRoles();
  const { data: idpGroupsBody } = dal.idpGroups.useList();
  const { data: samlBody } = dal.saml.useList();

  const providerNameById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const provider of samlBody?.samlIdentityProviders ?? []) {
      map[provider.id] = provider.providerName;
    }
    return map;
  }, [samlBody]);

  const samlGroupById = useMemo(() => {
    const map: Record<string, IdpGroup> = {};
    for (const group of idpGroupsBody?.groups ?? []) {
      map[group.id] = group;
    }
    return map;
  }, [idpGroupsBody]);

  const eligibleSamlGroups = useMemo(
    () =>
      (idpGroupsBody?.groups ?? []).filter((group) =>
        group.roleIds.some((roleId) =>
          rolesMap[roleId]?.permissionGrants.some(
            (grant) => grant.permission === PermissionType.MpaRequestsManage
          )
        )
      ),
    [idpGroupsBody, rolesMap]
  );

  const enabled = Boolean(ActionApprovalsSAML) && eligibleSamlGroups.length > 0;

  return { enabled, eligibleSamlGroups, samlGroupById, providerNameById };
};
